import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldAlert, RefreshCw, CheckCircle2, TrendingDown, TrendingUp, Search, Percent, Save,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_LABELS = {
  grv: "GRV",
  manual: "Manual edit",
  csv_price_import: "CSV price import",
  csv_retail_import: "CSV retail import",
  price_suggestion: "Price suggestion",
  fix_markups: "Fix Markups",
  first_sale_seed: "First-sale auto-seed",
  bulk_adjust: "Bulk adjust",
};

const FIELD_LABELS = {
  selling_price_incl_vat: "Selling (Incl VAT)",
  markup_percent: "Markup %",
  average_cost_price_excl_vat: "Avg Cost",
  last_cost_price_excl_vat: "Last Cost",
  cost_price: "Cost",
  transport_cost: "Transport Cost",
};

const R = (v, isPct = false) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (isPct) return `${n.toFixed(2)}%`;
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-ZA", {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function PriceChanges() {
  const { user } = useAuth();
  const [tab, setTab] = useState("needs_review");
  const [floor, setFloor] = useState(20);
  const [floorInput, setFloorInput] = useState("20");
  const [savingFloor, setSavingFloor] = useState(false);

  // Needs Review
  const [nrRows, setNrRows] = useState([]);
  const [nrTotal, setNrTotal] = useState(0);
  const [nrLoading, setNrLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, product: null, note: "" });
  const [confirming, setConfirming] = useState(false);

  // History feed
  const [feedRows, setFeedRows] = useState([]);
  const [feedTotal, setFeedTotal] = useState(0);
  const [feedLoading, setFeedLoading] = useState(false);
  const [filterSource, setFilterSource] = useState("__all__");
  const [filterField, setFilterField] = useState("__all__");
  const [filterPLU, setFilterPLU] = useState("");

  const loadSettings = async () => {
    try {
      const r = await axios.get(`${API}/price-changes/settings`);
      setFloor(r.data.minimum_markup_percent);
      setFloorInput(String(r.data.minimum_markup_percent));
    } catch (e) { /* ignore */ }
  };

  const loadNeedsReview = async () => {
    setNrLoading(true);
    try {
      const r = await axios.get(`${API}/price-changes/needs-review`, { params: { limit: 500 } });
      setNrRows(r.data.products || []);
      setNrTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Failed to load review list");
    } finally { setNrLoading(false); }
  };

  const loadFeed = async () => {
    setFeedLoading(true);
    try {
      const params = { limit: 500 };
      if (filterSource && filterSource !== "__all__") params.source = filterSource;
      if (filterField && filterField !== "__all__") params.field = filterField;
      if (filterPLU) params.plu_code = filterPLU;
      const r = await axios.get(`${API}/price-changes`, { params });
      setFeedRows(r.data.changes || []);
      setFeedTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Failed to load price change feed");
    } finally { setFeedLoading(false); }
  };

  useEffect(() => { loadSettings(); loadNeedsReview(); }, []);
  useEffect(() => { if (tab === "history") loadFeed(); /* eslint-disable-next-line */ }, [tab]);

  const saveFloor = async () => {
    const n = parseFloat(floorInput);
    if (!Number.isFinite(n) || n < 0 || n > 500) {
      toast.error("Floor must be a number between 0 and 500");
      return;
    }
    setSavingFloor(true);
    try {
      const r = await axios.put(`${API}/price-changes/settings`, { minimum_markup_percent: n });
      setFloor(r.data.minimum_markup_percent);
      toast.success(`Minimum markup floor set to ${n}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save floor");
    } finally { setSavingFloor(false); }
  };

  const openConfirm = (product) => setConfirmDialog({ open: true, product, note: "" });

  const submitConfirm = async () => {
    const { product, note } = confirmDialog;
    if (!product) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/price-changes/confirm/${product.id}`, {
        note,
        confirmed_by: user?.username || user?.name || "admin",
      });
      toast.success(`Confirmed low markup for ${product.name}`);
      setConfirmDialog({ open: false, product: null, note: "" });
      await loadNeedsReview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm");
    } finally { setConfirming(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="price-changes-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="price-changes-title">
            <ShieldAlert className="h-6 w-6 text-amber-600" />
            Price & Markup Audit
          </h1>
          <p className="text-sm text-slate-500">Track every price/markup change, and flag products dropping below the markup floor.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border rounded-md p-2 shadow-sm">
          <Percent className="h-4 w-4 text-slate-500" />
          <span className="text-sm text-slate-700">Minimum markup floor:</span>
          <Input
            type="number"
            value={floorInput}
            onChange={(e) => setFloorInput(e.target.value)}
            className="w-20 h-8"
            data-testid="floor-input"
          />
          <span className="text-sm text-slate-500">%</span>
          <Button
            size="sm"
            onClick={saveFloor}
            disabled={savingFloor || floorInput === String(floor)}
            data-testid="floor-save-btn"
          >
            <Save className="h-3.5 w-3.5 mr-1" /> Save
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="price-tabs">
          <TabsTrigger value="needs_review" data-testid="tab-needs-review">
            Needs Review ({nrTotal})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Change History</TabsTrigger>
        </TabsList>

        {/* NEEDS REVIEW */}
        <TabsContent value="needs_review" className="mt-3 space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Products with markup below {floor}%</span>
                <Button size="sm" variant="outline" onClick={loadNeedsReview} disabled={nrLoading} data-testid="nr-refresh">
                  <RefreshCw className={`h-4 w-4 mr-1 ${nrLoading ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </CardTitle>
              <p className="text-xs text-slate-500">
                Each of these was flagged automatically when its markup dropped below the floor.
                Confirm intentional low-margin items (they&apos;ll stop being flagged), or fix the price on Stock Master.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="needs-review-table">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                    <tr>
                      <th className="p-2 text-left">PLU</th>
                      <th className="p-2 text-left">Product</th>
                      <th className="p-2 text-left">Supplier</th>
                      <th className="p-2 text-right">Cost (Excl)</th>
                      <th className="p-2 text-right">Selling (Incl)</th>
                      <th className="p-2 text-right">Markup</th>
                      <th className="p-2 text-left">Flagged By</th>
                      <th className="p-2 text-left">When</th>
                      <th className="p-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nrLoading && (
                      <tr><td colSpan={9} className="p-8 text-center text-slate-400">Loading…</td></tr>
                    )}
                    {!nrLoading && nrRows.length === 0 && (
                      <tr>
                        <td colSpan={9} className="p-8 text-center text-slate-500">
                          <div className="flex flex-col items-center gap-2">
                            <CheckCircle2 className="h-8 w-8 text-green-500" />
                            <p>Nothing flagged. All products are at or above the {floor}% floor.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {!nrLoading && nrRows.map((p) => (
                      <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`nr-row-${p.plu_code || p.id}`}>
                        <td className="p-2 font-mono text-xs">{p.plu_code || "—"}</td>
                        <td className="p-2 font-medium">{p.name}</td>
                        <td className="p-2 text-slate-600">{p.supplier || "—"}</td>
                        <td className="p-2 text-right">{R(p.average_cost_price_excl_vat || p.last_cost_price_excl_vat)}</td>
                        <td className="p-2 text-right">{R(p.selling_price_incl_vat)}</td>
                        <td className="p-2 text-right font-semibold text-red-700">{R(p.markup_review_new_markup ?? p.markup_percent, true)}</td>
                        <td className="p-2 text-xs"><Badge variant="secondary">{SOURCE_LABELS[p.markup_review_source] || p.markup_review_source}</Badge></td>
                        <td className="p-2 text-xs text-slate-500">{fmtDate(p.markup_review_flagged_at)}</td>
                        <td className="p-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => openConfirm(p)} data-testid={`confirm-btn-${p.plu_code || p.id}`}>
                            Confirm intentional
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HISTORY */}
        <TabsContent value="history" className="mt-3 space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">All price/cost changes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={filterSource} onValueChange={setFilterSource}>
                  <SelectTrigger className="w-48 h-9" data-testid="filter-source"><SelectValue placeholder="All sources" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All sources</SelectItem>
                    {Object.entries(SOURCE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Select value={filterField} onValueChange={setFilterField}>
                  <SelectTrigger className="w-48 h-9" data-testid="filter-field"><SelectValue placeholder="All fields" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All fields</SelectItem>
                    {Object.entries(FIELD_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Filter by PLU…"
                    value={filterPLU}
                    onChange={(e) => setFilterPLU(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && loadFeed()}
                    className="pl-8 h-9 w-40"
                    data-testid="filter-plu"
                  />
                </div>
                <Button size="sm" onClick={loadFeed} disabled={feedLoading} data-testid="feed-apply">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${feedLoading ? "animate-spin" : ""}`} /> Apply
                </Button>
                <Badge variant="secondary">{feedTotal} total</Badge>
              </div>

              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm" data-testid="feed-table">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                    <tr>
                      <th className="p-2 text-left">When</th>
                      <th className="p-2 text-left">PLU</th>
                      <th className="p-2 text-left">Product</th>
                      <th className="p-2 text-left">Field</th>
                      <th className="p-2 text-right">Old</th>
                      <th className="p-2 text-right">New</th>
                      <th className="p-2 text-right">Δ</th>
                      <th className="p-2 text-left">Source</th>
                      <th className="p-2 text-left">User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedLoading && (
                      <tr><td colSpan={9} className="p-8 text-center text-slate-400">Loading…</td></tr>
                    )}
                    {!feedLoading && feedRows.length === 0 && (
                      <tr><td colSpan={9} className="p-8 text-center text-slate-500">No price changes yet.</td></tr>
                    )}
                    {!feedLoading && feedRows.map((c) => {
                      const isPct = c.field === "markup_percent";
                      const dir = c.delta > 0 ? "up" : c.delta < 0 ? "down" : null;
                      return (
                        <tr key={c.id} className="border-t hover:bg-slate-50">
                          <td className="p-2 text-xs text-slate-500 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                          <td className="p-2 font-mono text-xs">{c.plu_code || "—"}</td>
                          <td className="p-2">{c.product_name || "—"}</td>
                          <td className="p-2 text-xs">{FIELD_LABELS[c.field] || c.field}</td>
                          <td className="p-2 text-right">{R(c.old_value, isPct)}</td>
                          <td className="p-2 text-right font-medium">{R(c.new_value, isPct)}</td>
                          <td className={`p-2 text-right text-xs ${dir === "up" ? "text-green-700" : dir === "down" ? "text-red-700" : "text-slate-500"}`}>
                            {c.delta != null && (
                              <span className="inline-flex items-center gap-1">
                                {dir === "up" ? <TrendingUp className="h-3 w-3" /> : dir === "down" ? <TrendingDown className="h-3 w-3" /> : null}
                                {isPct ? `${c.delta.toFixed(2)}pp` : R(c.delta)}
                              </span>
                            )}
                          </td>
                          <td className="p-2 text-xs"><Badge variant="secondary">{SOURCE_LABELS[c.source] || c.source}</Badge></td>
                          <td className="p-2 text-xs text-slate-500">{c.user || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(o) => !o && setConfirmDialog({ open: false, product: null, note: "" })}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm intentional low markup</DialogTitle>
            <DialogDescription>
              {confirmDialog.product && (
                <>
                  <strong>{confirmDialog.product.name}</strong> (PLU {confirmDialog.product.plu_code}) has a markup
                  of <strong className="text-red-700">{(confirmDialog.product.markup_review_new_markup ?? confirmDialog.product.markup_percent ?? 0).toFixed(2)}%</strong>,
                  below the {floor}% floor. If this is intentional, add a note and confirm.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-slate-700">Reason / note (optional)</label>
            <Textarea
              value={confirmDialog.note}
              onChange={(e) => setConfirmDialog({ ...confirmDialog, note: e.target.value })}
              placeholder="e.g. Loss leader for footfall / clearance / competitor match"
              rows={3}
              data-testid="confirm-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, product: null, note: "" })}>Cancel</Button>
            <Button onClick={submitConfirm} disabled={confirming} className="bg-green-600 hover:bg-green-700" data-testid="confirm-submit">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm intentional
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
